import React from 'react';
import { motion } from 'framer-motion';
import { Target, Users, Zap, TrendingUp, Code, Briefcase, Lightbulb } from 'lucide-react';

const Skills = () => {
  const skillCategories = [
    {
      icon: Target,
      title: 'Strategic & Commercial',
      color: 'from-blue-100 to-blue-200',
      iconColor: 'text-blue-700',
      skills: [
        'Business Development & Ecosystem Development',
        'AI & Digital Transformation Strategy',
        'Strategic Growth & Market Entry',
        'Complex Negotiations & Commercial Contracts',
        'Competitive Intelligence & Market Analysis'
      ]
    },
    {
      icon: Users,
      title: 'Operational & Execution',
      color: 'from-primary-100 to-accent-100',
      iconColor: 'text-primary-700',
      skills: [
        'Chief of Staff & Executive Advisory',
        'Cross-functional Team Leadership',
        'Program Management & Execution',
        'Stakeholder Alignment & Governance',
        'Process Redesign & Efficiency Optimization'
      ]
    },
    {
      icon: Zap,
      title: 'Technical & Product',
      color: 'from-green-100 to-green-200',
      iconColor: 'text-green-700',
      skills: [
        'Product Vision & Roadmap Execution',
        'Autonomous & Connected Mobility',
        'Cloud & SaaS Product Strategy',
        'ML & Data Infrastructure Integration',
        'Technical Product Management'
      ]
    }
  ];

  const toolsTechnologies = {
    strategic: ['JIRA', 'Confluence', 'Trello', 'Notion', 'Pitchbook'],
    analytical: ['Tableau', 'SQL', 'Python', 'LLMs'],
    design: ['Figma'],
    enterprise: ['SAP']
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="section bg-gradient-to-br from-primary-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h1 className="section-title">Skills & Capabilities</h1>
            <p className="section-subtitle">
              Comprehensive expertise across strategic, operational, and technical domains
            </p>
            <p className="text-primary-700 text-lg leading-relaxed">
              I bring a robust mix of strategic acumen, operational leadership, and technical expertise—enabling me to deliver cross-functional initiatives that drive significant organizational value.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Skills Categories */}
      <section className="section bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {skillCategories.map((category, index) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${category.color} rounded-xl flex items-center justify-center mb-6`}>
                  <category.icon size={32} className={category.iconColor} />
                </div>
                <h3 className="text-xl font-semibold text-primary-900 mb-4">
                  {category.title}
                </h3>
                <ul className="space-y-2">
                  {category.skills.map((skill, skillIndex) => (
                    <li key={skillIndex} className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-primary-800">{skill}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="flex justify-center mt-8 mb-4"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-primary-400 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-primary-400 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>

      {/* Tools & Technologies */}
      <section className="section bg-primary-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <h2 className="section-title">Tools & Technologies</h2>
            <p className="section-subtitle">
              Platforms and technologies I leverage to drive strategic initiatives
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white rounded-xl p-8 border border-primary-100"
          >
            <div className="space-y-6">
              {Object.entries(toolsTechnologies).map(([category, tools]) => (
                <div key={category}>
                  <h3 className="text-lg font-semibold text-primary-900 mb-3 capitalize">
                    {category}
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {tools.map((tool) => (
                      <span
                        key={tool}
                        className="px-4 py-2 bg-primary-100 text-primary-800 rounded-full text-sm font-medium"
                      >
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h2 className="section-title">Ready to Leverage These Capabilities?</h2>
            <p className="section-subtitle">
              Let's discuss how my strategic, operational, and technical expertise can drive your organization's transformation
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="btn-primary"
              >
                Discuss an Opportunity
              </a>
              <a
                href="/projects"
                className="btn-secondary"
              >
                Explore Projects
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Skills; 